"use client";

import { PriorityRenderer } from "@faire/deprecated-retailer-visitor-shared/components/PriorityRenderer/PriorityRenderer";
import { decodeMultimodalQueryText } from "@faire/deprecated-retailer-visitor-shared/features/MultimodalProductSearch/multimodalQuery";
import { InsiderSlimBanner } from "@faire/deprecated-retailer-visitor-shared/features/SlimBanners/InsiderSlimBanner";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingDiscoverPageImprovements } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingDiscoverPageImprovements";
import { FilterPillsLoadingList } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/loggedInSearchLoadingSkeleton";
import { DiscoverPageFilteredProductGrids } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/DiscoverPageFilteredProductGrids/DiscoverPageFilteredProductGrids";
import { SearchProductsHeader } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/SearchPageHeader";
import { SearchPageResults } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageResults/SearchPageResults";
import { useSearchPagePerformanceTrackingRefCallback } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPagePerformanceTrackingWrapper";
import { useSearchPagePerformanceTracking } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPagePerformanceTracking";
import { useSearchPageSpellCorrectedQuery } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageSpellCorrectedQuery";
import { useSearchPageQueryIsLoading } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/useSearchPageQueryIsLoading";
import { useSearchPageIsFromDiscoverURL } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageProductSearchString } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { getHelmetData } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/util/getHelmetData";
import { useStrictLocalization } from "@faire/web/common/localization";
import { NoSSR } from "@faire/web/common/NoSSR";
import { LoadTimingRef } from "@faire/web/common/performance/userPerceivedLoadTiming/types";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Column } from "@faire/web/slate/Layout";
import React, { useMemo, Suspense } from "react";

import { LoggedOutSearchUPLFErrorBoundary } from "../../LoggedOutSearchUPLFErrorBoundary";

export const SearchPageHeaderAndResults = () => {
  useTraceComponentRender("SearchPageHeaderAndResults");

  const isLoading = useSearchPageQueryIsLoading();
  const spellCorrectedQuery = useSearchPageSpellCorrectedQuery();
  const productSearchString = useSearchPageProductSearchString();
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const isRSCProductTilesEnabled = useIsLoggedInRetailer();

  const { strictLocalize } = useStrictLocalization();

  const helmetData = useMemo(() => {
    const finalQuery =
      spellCorrectedQuery === undefined || isLoading
        ? productSearchString
        : spellCorrectedQuery;

    return getHelmetData(
      decodeMultimodalQueryText(finalQuery),
      isFromDiscoverURL,
      strictLocalize
    );
  }, [
    spellCorrectedQuery,
    isLoading,
    productSearchString,
    isFromDiscoverURL,
    strictLocalize,
  ]);

  if (isRSCProductTilesEnabled) {
    return (
      <RSCPerformanceWrapper
        helmetTitle={helmetData.title}
        helmetDescription={helmetData.description}
        helmetHeader={helmetData.header}
        isLoading={isLoading}
      />
    );
  } else {
    return (
      <LegacyPerformanceWrapper
        helmetTitle={helmetData.title}
        helmetDescription={helmetData.description}
        helmetHeader={helmetData.header}
        isLoading={isLoading}
      />
    );
  }
};

const RSCPerformanceWrapper = React.memo(
  ({
    helmetTitle,
    helmetDescription,
    helmetHeader,
    isLoading,
  }: {
    helmetTitle: string;
    helmetDescription: string;
    helmetHeader: string;
    isLoading: boolean;
  }) => {
    const rscSearchPageElementsRef =
      useSearchPagePerformanceTrackingRefCallback();
    return (
      <LoggedOutSearchUPLFErrorBoundary refCallback={rscSearchPageElementsRef}>
        <HeaderAndResults
          helmetTitle={helmetTitle}
          helmetDescription={helmetDescription}
          helmetHeader={helmetHeader}
          isLoading={isLoading}
          searchPageElementsRef={rscSearchPageElementsRef}
        />
      </LoggedOutSearchUPLFErrorBoundary>
    );
  }
);

const LegacyPerformanceWrapper = React.memo(
  ({
    helmetTitle,
    helmetDescription,
    helmetHeader,
    isLoading,
  }: {
    helmetTitle: string;
    helmetDescription: string;
    helmetHeader: string;
    isLoading: boolean;
  }) => {
    const legacySearchPageElementsRef = useSearchPagePerformanceTracking();
    return (
      <LoggedOutSearchUPLFErrorBoundary
        refCallback={legacySearchPageElementsRef}
      >
        <HeaderAndResults
          helmetTitle={helmetTitle}
          helmetDescription={helmetDescription}
          helmetHeader={helmetHeader}
          isLoading={isLoading}
          searchPageElementsRef={legacySearchPageElementsRef}
        />
      </LoggedOutSearchUPLFErrorBoundary>
    );
  }
);

const HeaderAndResults = React.memo(
  ({
    helmetTitle,
    helmetDescription,
    helmetHeader,
    isLoading,
    searchPageElementsRef,
  }: {
    helmetTitle: string;
    helmetDescription: string;
    helmetHeader: string;
    isLoading: boolean;
    searchPageElementsRef: LoadTimingRef;
  }) => {
    const isDiscoverPageImprovements = useSettingDiscoverPageImprovements();

    const helmetData = useMemo(() => {
      return {
        title: helmetTitle,
        description: helmetDescription,
        header: helmetHeader,
      };
    }, [helmetTitle, helmetDescription, helmetHeader]);

    return (
      <Column style={{ width: "100%" }}>
        {isDiscoverPageImprovements ? (
          <DiscoverPageFilteredProductGrids />
        ) : null}
        <NoSSR serverRenderFallback={<FilterPillsLoadingList />}>
          <Suspense fallback={<FilterPillsLoadingList />}>
            <SearchProductsHeader helmetData={helmetData} />
          </Suspense>
        </NoSSR>
        <PriorityRenderer>
          <InsiderSlimBanner />
        </PriorityRenderer>
        <SearchPageResults
          title={helmetData.header}
          searchPageLoadTimingRef={
            isLoading ? undefined : searchPageElementsRef
          }
        />
      </Column>
    );
  }
);
